/**
 * MdLite: renders the Learning Center markdown subset as React elements (plan
 * docs/plans/lms-plan.md Appendix E, D10, G13; WP-15).
 *
 * The source is tokenized by mdLiteTokenizer.ts (the twin of the backend's
 * markdown.js) and every token becomes a React element, so text is escaped
 * by React and nothing is ever injected as HTML: there is no raw-HTML escape
 * hatch anywhere under src/lms (a test greps for it). A link is only ever an
 * http or https URL (the tokenizer refuses anything else and the renderer
 * checks again) and opens in a new tab with rel="noopener noreferrer".
 *
 * A source the tokenizer refuses (a draft with a lint error) still renders:
 * as its plain text in one paragraph, marked data-md-invalid, so the preview
 * shows what was typed and nothing it contained can run.
 *
 * <MdLite source size> renders block markdown (the *_md fields, reading_md);
 * <MdLiteInline source> renders an inline field (a bullet, a table cell) in a
 * span. size 'sm' is the reading view's text-sm, 'base' the slide's text-base.
 */
import { Fragment, useMemo } from 'react';
import type { ReactNode } from 'react';
import { tokenizeInline, tokenizeMarkdown } from './mdLiteTokenizer';
import type { MdBlock, MdInline } from './types';

export type MdLiteSize = 'sm' | 'base';

const SAFE_HREF_RE = /^https?:\/\//i;
const EM_DASH = '\u2014';

const TEXT_CLS: Record<MdLiteSize, string> = {
  sm: 'text-sm leading-relaxed',
  base: 'text-base leading-relaxed',
};

const HEADING_CLS: Record<MdLiteSize, string> = {
  sm: 'text-sm font-semibold text-gray-900 dark:text-gray-100',
  base: 'text-base font-semibold text-gray-900 dark:text-gray-100',
};

const BODY_COLOR = 'text-gray-700 dark:text-gray-300';

/** React elements for inline tokens. */
export function renderInline(nodes: MdInline[], keyPrefix = 'i'): ReactNode[] {
  return nodes.map((node, index) => {
    const key = `${keyPrefix}.${index}`;
    switch (node.type) {
      case 'text':
        return <Fragment key={key}>{node.text}</Fragment>;
      case 'strong':
        return (
          <strong key={key} className="font-semibold text-gray-900 dark:text-gray-100">
            {renderInline(node.children, key)}
          </strong>
        );
      case 'em':
        return <em key={key}>{renderInline(node.children, key)}</em>;
      case 'code':
        return (
          <code
            key={key}
            className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.9em] text-gray-800 dark:bg-gray-700 dark:text-gray-100"
          >
            {node.text}
          </code>
        );
      case 'link':
        if (!SAFE_HREF_RE.test(node.href)) return <Fragment key={key}>{renderInline(node.children, key)}</Fragment>;
        return (
          <a
            key={key}
            href={node.href}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-primary-600 underline underline-offset-2 transition-colors duration-150 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
          >
            {renderInline(node.children, key)}
          </a>
        );
      default:
        return null;
    }
  });
}

/** React elements for block tokens. */
export function renderBlocks(blocks: MdBlock[], size: MdLiteSize = 'sm'): ReactNode[] {
  const text = `${TEXT_CLS[size]} ${BODY_COLOR}`;
  return blocks.map((block, index) => {
    const key = `b${index}`;
    switch (block.type) {
      case 'heading':
        return (
          <h3 key={key} className={HEADING_CLS[size]}>
            {renderInline(block.children, key)}
          </h3>
        );
      case 'paragraph':
        return (
          <p key={key} className={text}>
            {renderInline(block.children, key)}
          </p>
        );
      case 'list': {
        const items = block.items.map((item, i) => <li key={`${key}.${i}`}>{renderInline(item, `${key}.${i}`)}</li>);
        const listCls = `${text} space-y-1 pl-5 ${block.ordered ? 'list-decimal' : 'list-disc'}`;
        return block.ordered ? (
          <ol key={key} className={listCls}>
            {items}
          </ol>
        ) : (
          <ul key={key} className={listCls}>
            {items}
          </ul>
        );
      }
      case 'table':
        return (
          <div key={key} className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  {block.header.map((cell, c) => (
                    <th
                      key={`${key}.h${c}`}
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                    >
                      {renderInline(cell, `${key}.h${c}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {block.rows.map((row, r) => (
                  <tr key={`${key}.r${r}`}>
                    {row.map((cell, c) => (
                      <td key={`${key}.r${r}.${c}`} className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        {renderInline(cell, `${key}.r${r}.${c}`)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'blockquote':
        return (
          <figure key={key} className="border-l-4 border-gray-300 pl-4 dark:border-gray-600">
            <blockquote className={`${text} italic`}>{renderInline(block.children, key)}</blockquote>
            <figcaption className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {`${EM_DASH} `}
              {renderInline(block.attribution, `${key}.a`)}
            </figcaption>
          </figure>
        );
      default:
        return null;
    }
  });
}

export interface MdLiteProps {
  /** Block markdown in the Appendix E subset. */
  source: string;
  size?: MdLiteSize;
  className?: string;
  'data-testid'?: string;
}

/** Block markdown (the *_md fields and reading_md) as React elements. */
export function MdLite({ source, size = 'sm', className, 'data-testid': testId }: MdLiteProps) {
  const result = useMemo(() => tokenizeMarkdown(source), [source]);
  const wrapperCls = `space-y-3 ${className ?? ''}`.trim();
  if (!result.ok) {
    return (
      <div className={wrapperCls} data-testid={testId} data-md-invalid="true">
        <p className={`${TEXT_CLS[size]} ${BODY_COLOR} whitespace-pre-line`}>{typeof source === 'string' ? source : ''}</p>
      </div>
    );
  }
  return (
    <div className={wrapperCls} data-testid={testId}>
      {renderBlocks(result.blocks, size)}
    </div>
  );
}

export interface MdLiteInlineProps {
  /** One line of inline markdown (a bullet, a table cell). */
  source: string;
  className?: string;
}

/** Inline markdown (a bullet, a table cell) as React elements inside a span. */
export function MdLiteInline({ source, className }: MdLiteInlineProps) {
  const result = useMemo(() => tokenizeInline(source), [source]);
  if (!result.ok) {
    return (
      <span className={className} data-md-invalid="true">
        {typeof source === 'string' ? source : ''}
      </span>
    );
  }
  return <span className={className}>{renderInline(result.inlines)}</span>;
}

export default MdLite;
